import * as Tone from 'tone'
import { sampleLibrary} from "./sampleLibrary";


export const getSampleLibraryManagerInstance = () => {
    return sampleLibraryManagerInstance;
}

class SampleLibraryManager {

    soundBuffers = {};

    getSoundURL(soundName, defaultURL) {
        // Try find sound name in sample library
        const soundOfSampleLibrary = Object.values(sampleLibrary).flatMap(_=>_)
            .find((sound) => sound.name.toLowerCase() === soundName.toLowerCase());
        if (soundOfSampleLibrary) return window.location.origin + soundOfSampleLibrary.url;

        // Otherwise, return default sound
        return defaultURL ?? 'https://cdn.freesound.org/previews/262/262495_2331961-hq.mp3' // Dark pad 
    }

    // Loads sound if not loaded yet and returns the buffer.
    // Admits a callback for when the buffer loads
    getBufferForSound(soundName, onLoadCallback, defaultURL) {
        if (!this.soundBuffers.hasOwnProperty(soundName)) {
            const url = this.getSoundURL(soundName);
            console.debug("Carregant so de la llibreria: ", soundName, ", amb url:", url);
            this.soundBuffers[soundName] = new Tone.Buffer(url);
        };
        const buffer = this.soundBuffers[soundName];
        if (onLoadCallback == null) return buffer;
        // Si ja està carregat, crida directament el callback
        if (buffer.loaded) onLoadCallback(buffer);
        else {
            // Si existeix però no està carregat encara, encadenem els callbacks
            const originalOnload = buffer.onload;
            buffer.onload = () => {
                if (originalOnload) originalOnload();
                onLoadCallback(buffer);
            };
        }
        return buffer;
    }

}

export class SamplePlayer extends Tone.ToneAudioNode {
    static STRETCH_MODES = {
        STRETCHED: "stretched", // pitch and speed are independent controls
        UNSTRETCHED_REPITCH: "unstretched_repitch", // tape recorder effect, what happens by default when you change the speed in Tone.js. you only get the speed control
        UNSTRETCHED_RESPEED: "unstretched_respeed", // like unstretched_repitch, but the speed actually changes following the pitch
    }
    
    currentSoundInBufferName = "";
    #lastTriggerData = {
        position: 0,
        time: 0
    }

    _start = 0.0; // normalizing sound duration from 0 to 1
    _end = 1.0; // normalizing sound duration from 0 to 1
    _transpose = 0; // in semitones
    _speed = 1.0; // playback speed, where 1.0 is normal speed
    _stretchMode = SamplePlayer.STRETCH_MODES.STRETCHED;
    _doesNeedRetrigger = false;
    _fadeIn = 0;
    _fadeOut = 0;

    
    constructor() {
        super();
        this.gain = new Tone.Gain(Tone.dbToGain(0));
        this.fadeEnvelopeGain = new Tone.Gain();
        this.pitchShift = new Tone.PitchShift();
        this.player = new Tone.Player({volume: -6}).chain(this.pitchShift, this.fadeEnvelopeGain, this.gain);
        this.player.fadeIn = 0.01;
        this.player.fadeOut = 0.01;
        this.input = this.player;
        this.output = this.gain;
    }

    dispose() {
        this.pitchShift.dispose();
        this.player.dispose();
        this.gain.dispose();
    }


    get volume() {
        return Tone.gainToDb(this.gain.gain.value);
    }
    set volume(value) {
        this.gain.gain.rampTo(Tone.dbToGain(value), 0.01);
    }

    get transpose() {
        return this._transpose;
    }
    set transpose(value) {
        this._transpose = value;
        if (this.stretchMode === SamplePlayer.STRETCH_MODES.UNSTRETCHED_RESPEED && this.isPlaying()) this._doesNeedRetrigger = true;
        else this.applyPitchShift();
    }

    get start() {
        return this._start;
    }

    set start(value) {
        if (this._start === value) return;
        this._start = value;
        this.player.loopStart = this.getUnwarpedSoundDuration() * value;
        if (this.isPlaying()) this._doesNeedRetrigger = true;
    }

    get end() {
        return this._end;
    }

    set end(value) {
        if (this._end === value) return;
        this._end = value;
        this.player.loopEnd = this.getUnwarpedSoundDuration() * value;
        if (this.isPlaying()) this._doesNeedRetrigger = true;
    }

    get loop() {
        return this.player.loop;
    }
    set loop(value) {
        this.player.loop = value;
    }

    get isReverse() {
        return this.player.reverse;
    }
    set isReverse(value) {
        if (this.player.reverse === value) return;
        this.player.reverse = value;
        if (this.isPlaying()) this._doesNeedRetrigger = true;
    }

    get speed() {
        return this._speed;
    }
    set speed(value) {
        if (this._speed === value) return;
        this._speed = value;
        if (this.isPlaying()) this._doesNeedRetrigger = true;
    }
    get fadeIn() {
        return this._fadeIn;
    }
    set fadeIn(value) {
        this._fadeIn = value;
    }
    get fadeOut() {
        return this._fadeOut;
    }
    set fadeOut(value) {
        this._fadeOut = value;
    }

    applyPitchShift() {
        this.pitchShift.pitch = this.transpose + this.getPitchCompensationForSpeed();
    }

    applySpeed() {
        this.player.playbackRate = this.speed * this.getSpeedCompensationForPitch();
        this.player.loopStart = this.start * this.getUnwarpedSoundDuration();
        this.player.loopEnd = this.end * this.getUnwarpedSoundDuration();
        this.applyPitchShift();
    }

    get stretchMode() {
        return this._stretchMode;
    }
    
    set stretchMode(value) {
        if (this._stretchMode === value) return;
        this._stretchMode = value;
        if (this.isPlaying()) this._doesNeedRetrigger = true;
    }

    doesNeedRetrigger() {
        return this._doesNeedRetrigger;
    }

    loadSound(soundName) {
        getSampleLibraryManagerInstance().getBufferForSound(soundName, buffer => {
            // we create a duplicate because calling .reverse in the player actually reverses the buffer,
            // which we don't want for buffers that are shared for players, UI, etc.
            // new Tone.Buffer(buffer) just uses the same buffer, that's why we build it from the dumped Buffer
            this.player.buffer = Tone.Buffer.fromArray(buffer.toArray());
            this.currentSoundInBufferName = soundName;
            if (this.isPlaying()) this.stop();
        });
    }

    unloadSound() {
        this.currentSoundInBufferName = ""; // Clear current sound name
        if (this.isPlaying())  this.stop(0);
    }

    isBufferLoaded() {
        return this.player.buffer && this.player.buffer.loaded;
    }

    getUnwarpedSoundDuration() {
        if (!this.isBufferLoaded()) return 0;
        return this.player.buffer.duration;
    }

    // and not this.speed because it sometimes changes without the playback rate actually changing (needs a retrigger)
    getActualSoundDuration() {
        return this.getUnwarpedSoundDuration() / this.player.playbackRate;
    }

    isPlaying() {
        // Returns true if the player is currently playing sound, false otherwise. 
        // This is computed from the actual player node, not the "theoretical" playing state
        if (!this.isBufferLoaded()) return false; 
        return this.player.state === 'started';
    }

    play(time=Tone.now(), slicePosition=0) {
        // If buffer is not loaded yet, don't trigger the sound and return false
        if (! this.isBufferLoaded()) return false;

        // Configure attributes
        this.applySpeed();

        // Get info about start (needs attributes (see speed) configured)
        const position = this.start + (this.end - this.start) * slicePosition;
        const actualSoundDuration = this.getActualSoundDuration();
        const actualPositionInSeconds = position * actualSoundDuration;
        const actualDurationUntilSliceEnd = this.end * actualSoundDuration - actualPositionInSeconds;

        const unwarpedSoundDuration = this.getUnwarpedSoundDuration();
        const unwarpedPositionInSeconds = position * unwarpedSoundDuration;
        const unwarpedDurationUntilSliceEnd = this.end * unwarpedSoundDuration - unwarpedPositionInSeconds;

        // these 0.01 make it so there aren't any clicks/pops
        const fadeInTime = Math.max(0.01, this.fadeIn);
        const fadeOutTime = Math.max(0.01, this.fadeOut);
        const fadeOutStartTime = time + Math.max(0.01 + fadeInTime, actualDurationUntilSliceEnd - fadeOutTime);
        console.assert(this.fadeIn + this.fadeOut <= actualDurationUntilSliceEnd, "Fade in and out together are longer than sample slice");
        this.fadeEnvelopeGain.gain.cancelAndHoldAtTime(time); // for if there was a previous envelope scheduled
        this.fadeEnvelopeGain.gain.linearRampTo(0, 0.01, time);
        this.fadeEnvelopeGain.gain.linearRampTo(1, fadeInTime, time + 0.01);
        this.fadeEnvelopeGain.gain.linearRampTo(0, fadeOutTime, fadeOutStartTime);
        
        // Configure player attributes and trigger start. We trigger the sound without a duration set because the sound will be restarted anyway when required (end of the clip).
        // In this way, the player does not go to "stopped" in the middle of a beat and gets re-triggered again in a following step before the next time the sound should be 
        // actually retriggerd. This can happen if clip duration is reset while playing and due to roundings and stuff like that ¯\_(ツ)_/¯

        let playMethod = "start";
        if (this.isPlaying()) {
                // we don't use seek (plus associated linear ramp etc) because if clip duration is changed while playing it will bug
                playMethod = "restart";
        }
        this.player[playMethod](time, unwarpedPositionInSeconds, !this.loop ? unwarpedDurationUntilSliceEnd : undefined); 

        // Adjust stuff after start
        this._doesNeedRetrigger = false;
        this.#lastTriggerData = {
            position,
            time: this.player.now()
        }
        return true;
    }

    stop(time) {
        if (!this.isPlaying()) return; // If the player is not currently playing, do nothing
        this.player.stop(time);  // Triggering stop will add the fadeOut (release) time
    }

    get position() {
        // The Tone.js player object does not expose a "current playback position" parameter (nor does the underlying Web Audio API object).
        // Fortunately, because we only trigger the sounds in sync with the beat steps, we can estimate the playback position percentage based on
        // all sound parameters and some internal variables that we keep updated.
        if (!this.isPlaying()) return undefined;
        const speed = this.player.playbackRate; // and not this.speed because that can be changed in any moment
        if (speed === 0) return this.#lastTriggerData.position;
        const elapsedSeconds = this.player.toSeconds(this.player.now() - this.#lastTriggerData.time);
        const positionDifference = elapsedSeconds / this.getActualSoundDuration();
        return this.#lastTriggerData.position + positionDifference;
    }

    onSlicePosition(time, slicePosition) {
        const position = this.position;
        if (!this.isPlaying() || this.position < this.start || this.end < this.position || slicePosition === 0) this._doesNeedRetrigger = true;
        if (this.doesNeedRetrigger()) this.play(time, slicePosition);

        // else, it's playing and doesn't need to be retriggered
    }
    getPitchCompensationForSpeed() {
        if (this.stretchMode === SamplePlayer.STRETCH_MODES.UNSTRETCHED_REPITCH) return 0;
        if (this.stretchMode === SamplePlayer.STRETCH_MODES.UNSTRETCHED_RESPEED) return -this.transpose; // cancel out
        // If speed is 2.0 (2x), then pitch compensation should be -12 semitones (1 octave down). 
        // If speed is 0.5 (0.5x), then pitch compensation should be +12 semitones (1 octave up). 
        const speed = this.player.playbackRate; // and not this.speed because it might not be the current playback one
        return -12 * Math.log2(speed);
    }
    getSpeedCompensationForPitch() {
        if (this.stretchMode !== SamplePlayer.STRETCH_MODES.UNSTRETCHED_RESPEED) return 1;
        const pitch = this.transpose;
        return Math.pow(2, (pitch/12));
    }
}



const sampleLibraryManagerInstance = new SampleLibraryManager();